/**
 * Number formatting for a Persian-first UI (ADR-0012 §7).
 *
 * The rule the whole product follows: **quantities** are rendered with the user's preferred digits, while
 * **identifiers** (keys, ids, code, tokens) always stay Latin and isolated. That is why every function here
 * takes the preference explicitly instead of reading a global store: a component decides, and a test can pin the
 * outcome.
 */

/** Digit family used to render quantities. */
export type DigitStyle = 'persian' | 'latin';

/** Supported UI locales; the SPA is fa-first with English as the second locale. */
export type UiLocale = 'fa' | 'en';

/** Options accepted by every formatter in this module. */
export interface NumberFormatOptions {
  /** UI locale; selects the `Intl` locale (`fa-IR` or `en-US`). */
  locale?: UiLocale;
  /** Digit family to render. Defaults to Persian for `fa` and Latin for `en`. */
  digits?: DigitStyle;
  /** Minimum fraction digits passed to `Intl.NumberFormat`. */
  minimumFractionDigits?: number;
  /** Maximum fraction digits passed to `Intl.NumberFormat`. */
  maximumFractionDigits?: number;
}

const PERSIAN_DIGIT_START = 0x06f0;
const ARABIC_INDIC_DIGIT_START = 0x0660;
const LATIN_ZERO = 0x0030;

const PERSIAN_THOUSANDS_SEPARATOR = '\u066c';
const PERSIAN_DECIMAL_SEPARATOR = '\u066b';
const PERSIAN_PERCENT_SIGN = '\u066a';

/** Resolves the digit style that matches a locale when the caller expressed no preference. */
export function defaultDigitStyle(locale: UiLocale): DigitStyle {
  return locale === 'fa' ? 'persian' : 'latin';
}

/** Maps the application locale onto the BCP-47 tag used by `Intl`. */
export function intlLocaleFor(locale: UiLocale): string {
  return locale === 'fa' ? 'fa-IR' : 'en-US';
}

/** Converts Latin digits inside a string to Persian digits, leaving everything else untouched. */
export function toPersianDigits(input: string): string {
  return input.replace(/[0-9]/g, (digit) =>
    String.fromCharCode(PERSIAN_DIGIT_START + (digit.charCodeAt(0) - LATIN_ZERO)),
  );
}

/**
 * Converts Persian and Arabic-Indic digits to Latin digits.
 *
 * Both ranges are handled because a user may paste numerals produced on an Arabic keyboard layout, and a value
 * that silently fails to parse is worse than one that is normalized.
 */
export function toLatinDigits(input: string): string {
  return input.replace(/[\u06f0-\u06f9\u0660-\u0669]/g, (digit) => {
    const code = digit.charCodeAt(0);
    const offset =
      code >= PERSIAN_DIGIT_START ? code - PERSIAN_DIGIT_START : code - ARABIC_INDIC_DIGIT_START;

    return String.fromCharCode(LATIN_ZERO + offset);
  });
}

/** Applies the requested digit family to formatted output. */
function applyDigitStyle(value: string, digits: DigitStyle): string {
  return digits === 'persian' ? toPersianDigits(toLatinDigits(value)) : toLatinDigits(value);
}

/**
 * Formats a quantity with grouping and the locale's separators.
 *
 * @param value Number to format.
 * @param options Locale, digit family and fraction limits.
 * @returns The localized representation (for example `۱٬۲۳۵` in Persian, `1,235` in English).
 */
export function formatNumber(value: number, options: NumberFormatOptions = {}): string {
  const locale = options.locale ?? 'fa';
  const digits = options.digits ?? defaultDigitStyle(locale);
  const formatted = new Intl.NumberFormat(intlLocaleFor(locale), {
    minimumFractionDigits: options.minimumFractionDigits,
    maximumFractionDigits: options.maximumFractionDigits,
  }).format(value);

  return applyDigitStyle(formatted, digits);
}

/**
 * Formats a fraction as a percentage.
 *
 * @param value Fraction in the range 0–1 (so `0.42` renders as 42%).
 * @param options Locale and digit family, plus fraction limits.
 * @returns The localized percentage.
 */
export function formatPercent(value: number, options: NumberFormatOptions = {}): string {
  const locale = options.locale ?? 'fa';
  const digits = options.digits ?? defaultDigitStyle(locale);
  const formatted = new Intl.NumberFormat(intlLocaleFor(locale), {
    style: 'percent',
    minimumFractionDigits: options.minimumFractionDigits ?? 0,
    maximumFractionDigits: options.maximumFractionDigits ?? 0,
  }).format(value);

  return applyDigitStyle(formatted, digits);
}

/**
 * Formats a large quantity in compact notation (used by dashboard counters).
 *
 * @param value Number to format.
 * @param options Locale and digit family.
 * @returns A compact representation such as `۱۵ هزار` or `15K`.
 */
export function formatCompactNumber(value: number, options: NumberFormatOptions = {}): string {
  const locale = options.locale ?? 'fa';
  const digits = options.digits ?? defaultDigitStyle(locale);
  const formatted = new Intl.NumberFormat(intlLocaleFor(locale), {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);

  return applyDigitStyle(formatted, digits);
}

/**
 * Parses a number typed by a user, accepting Latin, Persian and Arabic-Indic digits and Persian separators.
 *
 * @param input Raw text from an input element.
 * @returns The parsed number, or `null` when the text is not a number.
 */
export function parseLocalizedNumber(input: string): number | null {
  const normalized = toLatinDigits(input.trim())
    .replaceAll(PERSIAN_THOUSANDS_SEPARATOR, '')
    .replaceAll(',', '')
    .replaceAll(PERSIAN_DECIMAL_SEPARATOR, '')
    .replaceAll(PERSIAN_PERCENT_SIGN, '');

  if (normalized.length === 0) {
    return null;
  }

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : null;
}
